import { v4 as uuidv4 } from 'uuid';
import { AUTOMATION_NODE_TYPE, AUTOMATION_NODE_KIND, BRANCH_KEY, TRIGGER_SOURCE_TYPE } from '@constants/automations';
import { pruneConditionGroup } from './conditions';

export const RF_NODE_TYPE = 'flowNode';
export const RF_EDGE_TYPE = 'insertEdge';

const EXIT = AUTOMATION_NODE_TYPE.EXIT;
const { BRANCH, TRIGGER, WAIT, SEND_MESSAGE, SEND_SURVEY } = AUTOMATION_NODE_TYPE;

const newId = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : uuidv4());

const rfNode = data => ({ id: data.id, type: RF_NODE_TYPE, position: { x: 0, y: 0 }, draggable: false, data });

const makeEdge = (source, target, branchKey = null) => ({
    id: newId(),
    type: RF_EDGE_TYPE,
    source,
    target,
    sourceHandle: branchKey || undefined,
    data: { branchKey: branchKey || null },
});

const KIND_TO_TYPE = {
    [AUTOMATION_NODE_KIND.SEND_MESSAGE]: SEND_MESSAGE,
    [AUTOMATION_NODE_KIND.SEND_COUPON]: SEND_MESSAGE,
    [AUTOMATION_NODE_KIND.SEND_SURVEY]: SEND_SURVEY,
    [AUTOMATION_NODE_KIND.WAIT]: WAIT,
    [AUTOMATION_NODE_KIND.BRANCH]: BRANCH,
};

export function createNode(kind) {
    const type = KIND_TO_TYPE[kind];
    const data = { id: newId(), type };
    if (type === WAIT) data.waitMinutes = 1440;
    if (type === BRANCH) data.conditions = { logic: 0, conditions: [], children: [] };
    if (type === SEND_MESSAGE)
        Object.assign(data, {
            message: '',
            subject: '',
            channelId: null,
            emailTemplateId: null,
            whatsAppTemplateRegistryId: null,
            couponTemplateId: null,
            isCoupon: kind === AUTOMATION_NODE_KIND.SEND_COUPON,
        });
    if (type === SEND_SURVEY)
        Object.assign(data, {
            surveyTemplateId: null,
            channelId: null,
            emailTemplateId: null,
            whatsAppTemplateRegistryId: null,
        });
    return rfNode(data);
}

const createTrigger = () =>
    rfNode({ id: newId(), type: TRIGGER, sourceType: TRIGGER_SOURCE_TYPE.CONTACT_ACTIVITY, activityTypes: [] });

const createExit = () => rfNode({ id: newId(), type: EXIT });

const isTerminal = type => type === TRIGGER || type === EXIT;

/**
 * Keeps the graph well-formed for rendering: exactly one Exit, every step's open path connected
 * to it (a branch always shows both yes/no), so the canvas always has a single terminal anchor.
 * Edges into Exit are recomputed here, never authored directly.
 */
export function normalizeFlow(nodes, edges) {
    let exit = nodes.find(n => n.data.type === EXIT);
    if (!exit) {
        exit = createExit();
        nodes = [...nodes, exit];
    }
    const exitId = exit.id;

    const kept = edges.filter(e => e.target !== exitId);
    const extra = [];

    for (const node of nodes) {
        if (node.id === exitId) continue;

        if (node.data.type === BRANCH) {
            for (const key of [BRANCH_KEY.YES, BRANCH_KEY.NO]) {
                if (!kept.some(e => e.source === node.id && e.data?.branchKey === key)) {
                    extra.push(makeEdge(node.id, exitId, key));
                }
            }
        } else if (!kept.some(e => e.source === node.id)) {
            extra.push(makeEdge(node.id, exitId));
        }
    }

    return { nodes, edges: [...kept, ...extra] };
}

export function insertNodeOnEdge(nodes, edges, edgeId, kind) {
    const edge = edges.find(e => e.id === edgeId);
    if (!edge) return { nodes, edges };

    const node = createNode(kind);
    const rest = edges.filter(e => e.id !== edgeId);

    rest.push(makeEdge(edge.source, node.id, edge.data?.branchKey));
    if (node.data.type === BRANCH) {
        rest.push(makeEdge(node.id, edge.target, BRANCH_KEY.YES));
        rest.push(makeEdge(node.id, edge.target, BRANCH_KEY.NO));
    } else {
        rest.push(makeEdge(node.id, edge.target));
    }

    return { ...normalizeFlow([...nodes, node], rest), selectedId: node.id };
}

export function canDeleteNode(nodes, edges, nodeId) {
    const node = nodes.find(n => n.id === nodeId);
    if (!node || isTerminal(node.data.type)) return false;
    if (node.data.type !== BRANCH) return true;
    const exitId = nodes.find(n => n.data.type === EXIT)?.id;
    return edges.filter(e => e.source === nodeId).every(e => e.target === exitId);
}

export function deleteNode(nodes, edges, nodeId) {
    if (!canDeleteNode(nodes, edges, nodeId)) return { nodes, edges };

    const incoming = edges.filter(e => e.target === nodeId);
    const outgoing = edges.filter(e => e.source === nodeId);
    const continuation = outgoing.find(e => !e.data?.branchKey) || outgoing[0];

    const rest = edges.filter(e => e.target !== nodeId && e.source !== nodeId);
    for (const inEdge of incoming) {
        rest.push(makeEdge(inEdge.source, continuation.target, inEdge.data?.branchKey));
    }

    return normalizeFlow(
        nodes.filter(n => n.id !== nodeId),
        rest,
    );
}

export function emptyAutomation() {
    const trigger = createTrigger();
    const exit = createExit();
    const { nodes, edges } = normalizeFlow([trigger, exit], [makeEdge(trigger.id, exit.id)]);
    return {
        model: {
            id: '00000000-0000-0000-0000-000000000000',
            title: '',
            status: 0,
            onePerContact: false,
            entryConditions: null,
        },
        nodes,
        edges,
    };
}

export function modelToFlow(model) {
    const nodes = (model.nodes || []).map(node =>
        rfNode({ ...node, isCoupon: node.type === SEND_MESSAGE && !!node.couponTemplateId }),
    );
    nodes.push(createExit());

    const edges = (model.edges || []).map(edge => makeEdge(edge.sourceNodeId, edge.targetNodeId, edge.branchKey));

    return normalizeFlow(nodes, edges);
}

export function flowToModel(base, nodes, edges) {
    const exitId = nodes.find(n => n.data.type === EXIT)?.id;
    const realNodes = nodes.filter(n => n.data.type !== EXIT);

    return {
        ...base,
        entryConditions: pruneConditionGroup(base.entryConditions),
        nodes: realNodes.map(node => {
            const data = { ...node.data };
            delete data.isCoupon;
            return {
                ...data,
                canvasX: node.position?.x || 0,
                canvasY: node.position?.y || 0,
                conditions: pruneConditionGroup(data.conditions),
            };
        }),
        edges: edges
            .filter(e => e.target !== exitId)
            .map(edge => ({
                id: edge.id,
                sourceNodeId: edge.source,
                targetNodeId: edge.target,
                branchKey: edge.data?.branchKey || null,
            })),
    };
}
